import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Typography,
  TableSortLabel,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import type { UserListTableProps } from './types';
import {
  USER_ACCESS_PAGINATION,
  USER_TABLE_STYLE,
  USER_STATUS_COLOR_MAP,
} from '@/config/constant';
import './UserAccess.css';

export const UserListTable: React.FC<UserListTableProps> = ({
  users,
  totalCount,
  loading,
  searchQuery,
  page,
  rowsPerPage,
  sortField,
  sortOrder,
  onDeleteClick,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
}) => {
  return (
    <>
      <TableContainer
        sx={{
          maxHeight: USER_TABLE_STYLE.MAX_HEIGHT,
          overflowY: 'auto',
          border: loading ? USER_TABLE_STYLE.BORDER_LOADING : USER_TABLE_STYLE.BORDER_NORMAL,
          borderRadius: 1,
          transition: 'border 0.3s ease-in-out',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                <TableSortLabel
                  active={sortField === 'email'}
                  direction={sortField === 'email' ? sortOrder : 'asc'}
                  onClick={() => onSort('email')}
                  aria-label="Sort by email"
                  className="user-access-sort-label"
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }} className="user-access-status-col">
                <TableSortLabel
                  active={sortField === 'status'}
                  direction={sortField === 'status' ? sortOrder : 'asc'}
                  onClick={() => onSort('status')}
                  aria-label="Sort by status"
                  className="user-access-sort-label"
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: 'grey.100' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Typography variant="body2" color="textSecondary" sx={{ py: 4 }}>
                    {searchQuery ? 'No users found matching your search' : 'No users found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.email} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell align="center" className="user-access-status-col">
                    <Chip
                      label={user.status}
                      size="small"
                      color={USER_STATUS_COLOR_MAP[user.status.toLowerCase()] ?? 'default'}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="error"
                      onClick={() => onDeleteClick(user.email)}
                      disabled={loading}
                      aria-label="delete user"
                      size="small"
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={USER_ACCESS_PAGINATION.ROWS_PER_PAGE_OPTIONS}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onChangePage}
        onRowsPerPageChange={onChangeRowsPerPage}
      />
    </>
  );
};